import React, { useState, useEffect } from "react";
import { Client, Databases } from "appwrite";
import colorlogo from "../assets/Academy/colorlogo.png";
import close from "../assets/Academy/close.webp";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import config from "../config/config";
import { 
  parsePhoneNumberFromString, 
  isValidPhoneNumber 
} from "libphonenumber-js";

const CareerForm = ({ isOpen, onClose, selectedInterest, documentId, utmContent }) => {
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [businessemail, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [jobtitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  // Check if the user selected individual booking
  const isIndividualBooking = selectedInterest === "individual";

  const [utmParams, setUtmParams] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_id: "",
    utm_term: "",
    utm_content: "",
  });

  // Appwrite client setup
  const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

  const databases = new Databases(client);

  // UTM Parameters Capture
  useEffect(() => {
    const captureUtmParams = () => {
      const params = new URLSearchParams(window.location.search);
      const utmFields = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_id",
        "utm_term",
      ];

      const newUtmParams = {
        utm_content: utmContent,
      };
      utmFields.forEach((field) => {
        newUtmParams[field] = params.get(field) || "";
      });
      
      setUtmParams(newUtmParams);
    };

    captureUtmParams();
    window.addEventListener("popstate", captureUtmParams);

    return () => {
      window.removeEventListener("popstate", captureUtmParams);
    };
  }, [utmContent]);

  // Phone Number Validation
  const handlePhoneNumberChange = (number, country) => {
    setPhoneNumber(number);

    try {
      const fullNumber = number.startsWith("+") ? number : `+${number}`;

      if (isValidPhoneNumber(fullNumber)) {
        const phoneNumberObj = parsePhoneNumberFromString(fullNumber);
        const nationalNumber = phoneNumberObj.nationalNumber;

        const expectedLength = phoneNumberObj.nationalNumber?.length;
        const actualLength = nationalNumber?.length;

        if (actualLength < expectedLength) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: `Phone number is too short - needs ${expectedLength} digits`,
          }));
        } else if (actualLength > expectedLength) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: `Phone number is too long - should be ${expectedLength} digits`,
          }));
        } else {
          setErrors((prev) => ({ ...prev, phoneNumber: "" }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Please enter a valid phone number",
        }));
      }
    } catch (error) {
      console.error("Phone validation error:", error);
      setErrors((prev) => ({
        ...prev,
        phoneNumber: "Please enter a valid phone number",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      if (!phoneNumber) {
        setErrors({ phoneNumber: "Phone number is required" });
        setIsLoading(false);
        return;
      }

      const fullNumber = phoneNumber.startsWith("+") 
        ? phoneNumber 
        : `+${phoneNumber}`;

      if (!isValidPhoneNumber(fullNumber)) {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Please enter a valid phone number",
        }));
        setIsLoading(false);
        return;
      }


      const formData = {
        firstname,
        lastname,
        businessemail,
        phoneNumber,
        jobtitle,
        companyName,
        interestType: selectedInterest, 
        utmSource: utmParams.utm_source,
        utmMedium: utmParams.utm_medium,
        utmCampaign: utmParams.utm_campaign,
        utmId: utmParams.utm_id,
        utmTerm: utmParams.utm_term,
        utmContent: utmParams.utm_content,
      };

      await databases.updateDocument(
        process.env.REACT_APP_DATABASE_ID,
        config.collectionIds.academyBookACall,
        documentId, 
        formData
      );
      

      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-booking`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          documentId
        }),
      });
      setIsSuccessOpen(true);
      resetForm();
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Failed to submit form. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

      

 
  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhoneNumber("");
    setJobTitle("");
    setCompanyName("");
    setErrors({});
  };

  const closeSuccessModal = () => {
    setIsSuccessOpen(false);
    onClose();
  };

  if (!isOpen) return null;
  

  if (isSuccessOpen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
        <div className="relative bg-white p-6 md:p-10 rounded-lg max-w-md w-11/12 md:w-full text-center">
          <a
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
            onClick={closeSuccessModal}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </a>
          <img src={colorlogo} alt="Logo" className="w-24 md:w-32 mx-auto mb-4 md:mb-6" />
          <p className="mb-6 italic text-base md:text-lg">
          "We've got your request! One of our learning experts will be in touch soon to chat about the course and answer any questions you might have."
          </p>
          <button
            className="px-6 py-2 bg-[rgba(62,89,183,1)] text-white rounded hover:bg-blue-700"
            onClick={closeSuccessModal}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full md:w-full max-w-4xl p-2 relative flex flex-col md:flex-row overflow-y-auto max-h-full md:max-h-full">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 text-gray-600 hover:text-gray-900 z-10"
        >
          <img src={close} alt="Close" className="w-3 h-3" />
        </button>
        <div className="w-full md:w-4/5 flex flex-col justify-center items-center md:items-start text-center md:text-left md:border-r md:border-gray-300 py-6 md:py-10 px-4 md:pr-20 md:pl-6">
  <img src={colorlogo} alt="Logo" className="w-40 md:w-9/12 mb-4" />
  <h2 className="text-lg md:text-xl font-bold text-[rgba(62,89,183,1)]">
    Book a call with our team today!
  </h2>
  <p className="text-gray-700 text-sm mt-2">
    Book a call with one of our team members to help provide you
    information on enterprise solutions.
  </p>
</div>
        <div className="w-full md:w-4/5  md:p-6">
            <h2 className="text-lg font-semibold mb-4 text-center md:text-left">
              Please fill the form to proceed payment
            </h2>
            <form onSubmit={handleSubmit} className="space-y-2">
            
                 <div className="mb-0">
                  <label className="text-gray-700 text-sm">First Name</label>
                  <input
                    id="firstname"
                    type="text"
                    name="firstname"
                    value={firstname}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    required
                  />
                  {errors.firstname && (
                    <p className="text-red-500 text-xs">{errors.firstname}</p>
                  )}
                </div>
              <div className="mb-0">
                  <label className="text-gray-700 text-sm">Last Name</label>
                  <input
                    type="text"
                    name="lastname"
                    id="lastname3"
                    value={lastname}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    required
                  />
                  {errors.lastname && (
                    <p className="text-red-500 text-xs">{errors.lastname}</p>
                  )}
                </div>
        
              <div className="mb-0">
                  <label className="text-gray-700 text-sm">
                    {isIndividualBooking ? "Email" : "Business Email"}
                  </label>
                  <input
                    id="businessemail"
                    type="email"
                    name="businessemail"
                    value={businessemail}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    required
                  />
                  {errors.businessemail && (
                    <p className="text-red-500 text-xs">
                      {errors.businessemail}
                    </p>
                  )}
                </div>
                <div className="mb-0">
                  <label id="Phonenumber" className="text-gray-700 text-sm">
                    Phone Number
                  </label>
                  <PhoneInput
                    country={"in"}
                    value={phoneNumber}
                    onChange={(number, country) =>
                      handlePhoneNumberChange(number, country)
                    }
                    inputClass="custom-phone-input2 w-full"
                    containerClass="w-full"
                    buttonClass="border rounded"
                    dropdownClass="border rounded"
                    required
                    enableSearch={true}
                    inputProps={{
                      required: true,
                      autoFocus: false,
                    }}
                  />
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-xs">{errors.phoneNumber}</p>
                  )}
                </div>
             
                <div className="mb-0">
                  <label className="text-gray-700 text-sm">Job Title</label>
                  <input
                    type="text"
                    name="Job Title" 
                    id="Job Title" 
                    value={jobtitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    required
                  />
                  {errors.jobtitle && (
                    <p className="text-red-500 text-xs">{errors.jobtitle}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="text-gray-700 text-sm">Company Name</label>
                  <input
                    id="Companyname"
                    type="text"
                    name="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    required
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-xs">{errors.companyName}</p>
                  )}
                </div>
            
              <button 
                type="submit" 
                className={`w-full p-2 bg-[rgba(62,89,183,1)] text-white rounded hover:bg-blue-700 ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Send & Schedule a Callback"}
              </button>
            </form>
          </div>
      </div>
    </div>
  );
};

export default CareerForm;